import { useTranslation } from 'next-i18next';

import { useCallback, useState } from 'react';

import { Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';

import { AdaptiveDrawer } from '@nocowanie/common-ui';

import { AppModalsEnum } from '@app/enums/app-modals.enum';
import { useDrawerState, useModalFragmentSync } from '@app/hooks';
import {
    accommodationStaticSlice,
    commonUiStateSlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

export const OperatorInformation = () => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'operatorInformation',
    });
    const { t: tCommon } = useTranslation('common');
    const [isExpanded, setIsExpanded] = useState(false);
    const operatorInformation = useAppSelector(
        accommodationStaticSlice.selectors.selectOperatorInformation,
    );
    const isDrawerOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.OperatorInformation),
    );
    const dispatch = useAppDispatch();
    useModalFragmentSync(AppModalsEnum.OperatorInformation, isDrawerOpen);

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.OperatorInformation,
                modalData: true,
            }),
        );
    });
    const closeDrawer = useCallback(() => {
        setTimeout(
            () =>
                dispatch(
                    commonUiStateSlice.actions.resetModalDataAction(
                        AppModalsEnum.OperatorInformation,
                    ),
                ),
            0,
        );
    }, []);

    const operatorFields = [
        {
            labelKey: 'labels.companyName',
            value: operatorInformation?.companyName,
        },
        {
            labelKey: 'labels.address',
            value:
                operatorInformation?.street ||
                operatorInformation?.postalCode ||
                operatorInformation?.settlementName
                    ? [
                          operatorInformation.postalCode,
                          operatorInformation.settlementName,
                          operatorInformation.street,
                      ]
                          .filter(Boolean)
                          .join(', ')
                    : null,
        },
        {
            labelKey: 'labels.nip',
            value: operatorInformation?.taxNumber,
        },
        {
            labelKey: 'labels.companyRegisterNumber',
            value: operatorInformation?.companyRegisterNumber,
        },
    ];

    const contactFields = [
        {
            labelKey: 'labels.phone',
            value: operatorInformation?.phone,
        },
        {
            labelKey: 'labels.email',
            value: operatorInformation?.email,
        },
    ];

    return (
        <>
            <Box gap={1}>
                {operatorInformation?.companyRegisterNumber || operatorInformation?.taxNumber
                    ? t('managedByCompany')
                    : t('managedByOwner')}{' '}
                <Button onClick={openDrawer} disabled={isDrawerPending} variant="link">
                    {t('learnMore')} {isDrawerPending ? <Spinner ml={1} size={'xs'} /> : null}
                </Button>
            </Box>
            <AdaptiveDrawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                header={t('modalHeader')}
                drawerProps={{
                    size: {
                        md: 'md',
                    },

                    placement: {
                        base: 'bottom',
                        md: 'end',
                    },
                }}
                mb={{
                    base: 6,
                    md: 0,
                }}
            >
                <Text
                    fontSize={{
                        base: 'lg',
                        md: 'xl',
                    }}
                    fontWeight={'bold'}
                    mb={2}
                >
                    {t('title')}
                </Text>
                <VStack align="stretch" gap={1}>
                    {operatorFields
                        .filter(field => field.value)
                        .map(field => (
                            <Text key={field.labelKey} as="span">
                                {t(field.labelKey)}: {field.value}
                            </Text>
                        ))}
                    {isExpanded
                        ? contactFields
                              .filter(field => field.value)
                              .map(field => (
                                  <Text key={field.labelKey} as="span">
                                      {t(field.labelKey)}: {field.value}
                                  </Text>
                              ))
                        : null}
                </VStack>
                {contactFields.some(field => field.value) ? (
                    <Button variant="link" onClick={() => setIsExpanded(!isExpanded)}>
                        {tCommon(isExpanded ? 'list-button.less' : 'list-button.more')}
                    </Button>
                ) : null}
            </AdaptiveDrawer>
        </>
    );
};
